import * as React from 'react'
import * as auth0 from 'auth0-js'
import jwtDecode from 'jwt-decode'
import { navigate } from 'gatsby'

const PREVIOUS_PATH_KEY = 'auth0_previous_path'
const AUTH_TOKEN_KEY = 'auth0_AuthToken'

export enum Roles {
  OUTLET = 'OUTLET',
  ADMIN = 'ADMIN',
  ORDERADMIN = 'ORDERADMIN',
  ACCOUNTANT = 'ACCOUNTANT',
  GUIDE = 'GUIDE',
}

interface Auth0Options {
  domain: string
  clientID: string
}

interface Auth0ProviderProps {
  children: JSX.Element
  auth0Options: Auth0Options
}

interface Auth0ContextType {
  login: () => Promise<void>
  logout: () => void
  hasRole: (...requestedRoles: Roles[]) => boolean
  user: AuthenticatedUser | null
  needMailVerification?: boolean
}

type AuthenticatedUser = {
  accessToken: string
  email: string
  roles: Roles[]
  outletId: string | undefined
}

type Token = {
  'https://gcb-accounting-production.azurewebsites.net/roles': string
  'https://gcb-accounting-production.azurewebsites.net/outletId': string
  exp: number
}

const Auth0Context = React.createContext({} as Auth0ContextType)

export const useAuth0 = () => React.useContext(Auth0Context)

export const Auth0Provider: React.FC<Auth0ProviderProps> = ({
  children,
  auth0Options,
}: Auth0ProviderProps) => {
  const [authClient, setAuthClient] = React.useState<auth0.WebAuth>()
  const [user, setUser] = React.useState<AuthenticatedUser | null>(null)
  const [needMailVerification, setNeedMailVerification] = React.useState(false)

  React.useEffect(() => {
    const toUser = (
      authResult: auth0.Auth0DecodedHash | null
    ): AuthenticatedUser | null => {
      if (authResult) {
        const accessTokenDecoded = jwtDecode<Token>(authResult.accessToken)
        return {
          accessToken: authResult.accessToken,
          email: authResult.idTokenPayload?.email,
          roles: accessTokenDecoded[
            'https://gcb-accounting-production.azurewebsites.net/roles'
          ].split(',') as Roles[],
          outletId:
            accessTokenDecoded[
              'https://gcb-accounting-production.azurewebsites.net/outletId'
            ],
        }
      }
      return null
    }

    const initAuth0 = async () => {
      const options = {
        responseType: 'token id_token',
        audience: 'https://gcb-accounting-production.azurewebsites.net/graphql',
        scope: 'openid email profile',
        redirectUri: `${window.location.origin}/de/callback`,
        ...auth0Options,
      }

      const newAuthClient = new auth0.WebAuth(options)
      setAuthClient(newAuthClient)
      const key = localStorage.getItem(AUTH_TOKEN_KEY)
      if (key) {
        const authResult: auth0.Auth0DecodedHash = JSON.parse(key)
        const accessTokenDecoded = jwtDecode<Token>(authResult.accessToken)
        const tokenValidForSeconds =
          0 + accessTokenDecoded.exp - new Date().getTime() / 1000
        if (tokenValidForSeconds > 60) {
          setUser(toUser(authResult))
        }
      }

      if (
        window.location.hash.includes('access_token=') ||
        window.location.hash.includes('error=unauthorized')
      ) {
        newAuthClient.parseHash(
          { hash: window.location.hash },
          (err, authResult) => {
            if (err) {
              console.error('Authentication error', err)
              if (
                err.errorDescription ===
                'Please verify your email before logging in.'
              ) {
                setNeedMailVerification(true)
              }
            } else {
              setUser(toUser(authResult))
              localStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(authResult))
              setNeedMailVerification(false)
              const previousPath = localStorage.getItem(PREVIOUS_PATH_KEY)
              localStorage.removeItem(PREVIOUS_PATH_KEY)

              navigate(previousPath || '/')
            }
          }
        )
      }
    }
    initAuth0()
  }, [])

  const login = async () => {
    console.log('set previous path to', window.location.pathname)
    localStorage.setItem(PREVIOUS_PATH_KEY, window.location.pathname)
    return authClient.authorize()
  }

  const logout = async () => {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.removeItem(PREVIOUS_PATH_KEY)
    const returnPath = `${window.location.origin}/de`
    console.log('logout and return to', returnPath)
    authClient.logout({ returnTo: returnPath })
  }

  const hasRole = (...requestedRoles: Roles[]) => {
    if (requestedRoles.length === 0) {
      return true
    }
    return requestedRoles.some((role) => user?.roles.some((gr) => gr === role))
  }

  return (
    <Auth0Context.Provider
      value={{
        login,
        logout,
        hasRole,
        user,
        needMailVerification,
      }}
    >
      {children}
    </Auth0Context.Provider>
  )
}
